/**
 * ApplicationDetailsModal Component
 *
 * Modal for viewing application details overview.
 */

import React from "react";
import { Modal } from "../../../components/ui/Modal";
import { Button } from "../../../components/ui/Button";
import { Badge } from "../../../components/ui/Badge";
import { Alert } from "../../../components/ui/Alert";
import { Spinner } from "../../../components/ui/Spinner";
import { CopyableCode } from "../../../components/ui/CopyableCode";
import { useApplication } from "../hooks/useApplication";
import { formatDate } from "../../../lib/utils/format";
import { getErrorMessage } from "../../../lib/utils/errors";

interface ApplicationDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  tenantId: string;
  applicationId: string;
}

/**
 * Get badge variant based on deployment status
 */
const getStatusBadgeVariant = (status: string): "success" | "warning" | "error" | "info" => {
  switch (status) {
    case "deployed":
      return "success";
    case "deploying":
      return "warning";
    case "failed":
      return "error";
    case "pending":
    default:
      return "info";
  }
};

/**
 * Format deployment status for display
 */
const formatStatus = (status: string): string => {
  return status.charAt(0).toUpperCase() + status.slice(1);
};

/**
 * ApplicationDetailsModal component
 */
export const ApplicationDetailsModal: React.FC<ApplicationDetailsModalProps> = ({ isOpen, onClose, tenantId, applicationId }) => {
  // Fetch application details
  const { data: application, isLoading, error } = useApplication(tenantId, applicationId);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <Modal.Header>Application Details</Modal.Header>

      <Modal.Body>
        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        )}

        {/* Error State */}
        {error && <Alert variant="error">{getErrorMessage(error)}</Alert>}

        {/* Content */}
        {!isLoading && application && (
          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Application Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{application.applicationName}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Application Code</dt>
                  <dd className="mt-1">
                    <CopyableCode value={application.applicationCode} />
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Application ID</dt>
                  <dd className="mt-1">
                    <CopyableCode value={application.applicationId} />
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="mt-1 flex items-center gap-2">
                    <Badge variant={getStatusBadgeVariant(application.deploymentStatus)}>
                      {formatStatus(application.deploymentStatus)}
                    </Badge>
                    {!application.isActive && <Badge variant="warning">Inactive</Badge>}
                  </dd>
                </div>
                {application.description && (
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-500">Description</dt>
                    <dd className="mt-1 text-sm text-gray-900">{application.description}</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Configuration */}
            {(application.actions?.length > 0 || application.menu || application.submenu) && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Configuration</h3>
                <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {application.actions && application.actions.length > 0 && (
                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-gray-500">Supported Actions</dt>
                      <dd className="mt-1 flex flex-wrap gap-2">
                        {application.actions.map((action) => (
                          <Badge key={action} variant="info">
                            {action}
                          </Badge>
                        ))}
                      </dd>
                    </div>
                  )}
                  {application.menu && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Menu Category</dt>
                      <dd className="mt-1 text-sm text-gray-900">{application.menu}</dd>
                    </div>
                  )}
                  {application.submenu && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Submenu Category</dt>
                      <dd className="mt-1 text-sm text-gray-900">{application.submenu}</dd>
                    </div>
                  )}
                </dl>
              </div>
            )}

            {/* Deployment */}
            {(application.deployedAt || application.functionArn || application.apiEndpoint || application.codeBundle) && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Deployment</h3>
                <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {application.deployedAt && (
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Deployed At</dt>
                      <dd className="mt-1 text-sm text-gray-900">
                        {formatDate(application.deployedAt, 'MMM dd, yyyy HH:mm')}
                      </dd>
                    </div>
                  )}
                  {application.functionArn && (
                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-gray-500">Lambda Function ARN</dt>
                      <dd className="mt-1 text-sm text-gray-900 font-mono break-all">
                        {application.functionArn}
                      </dd>
                    </div>
                  )}
                  {application.apiEndpoint && (
                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-gray-500">API Endpoint</dt>
                      <dd className="mt-1">
                        <a
                          href={application.apiEndpoint}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-primary-600 hover:text-primary-800 font-mono break-all hover:underline"
                        >
                          {application.apiEndpoint}
                        </a>
                      </dd>
                    </div>
                  )}
                  {application.codeBundle && (
                    <div className="sm:col-span-2">
                      <dt className="text-sm font-medium text-gray-500">Code Bundle</dt>
                      <dd className="mt-1 text-sm text-gray-900 font-mono break-all">
                        {application.codeBundle}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            )}

            {/* Metadata */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Metadata</h3>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Created At</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(application.createdAt, 'MMM dd, yyyy HH:mm')}
                  </dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Updated At</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(application.updatedAt, 'MMM dd, yyyy HH:mm')}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
